/**
 * SideDrawer.tsx
 *
 * Right-side panel over a full-frame scrim (docs/DESIGN.md + the S0
 * shell-snippets.html "(l) Notifications drawer" reference), 400px wide by
 * default, full height, slide-in from the right. Same X-as-inline-SVG
 * convention as SheetDialog — no icon dependency.
 */

import type { ReactNode } from 'react';
import { useDialogFocus } from './useDialogFocus';

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  closeLabel: string;
  children: ReactNode;
  width?: number;
}

export default function SideDrawer({
  open,
  onClose,
  title,
  closeLabel,
  children,
  width = 400,
}: SideDrawerProps) {
  const panelRef = useDialogFocus(open, onClose);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-60 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative h-full w-full bg-surface flex flex-col shadow-xl drawer-in outline-none"
        style={{ maxWidth: width }}
      >
        <div className="flex h-14 items-center gap-2 px-4 border-b border-border">
          <p className="flex-1 truncate text-base font-semibold text-content">{title}</p>
          <button
            onClick={onClose}
            aria-label={closeLabel}
            className="size-11 flex items-center justify-center rounded-pill text-muted hover:bg-bg"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
